import { Resolver } from "react-hook-form";
import { parse, isBefore, isAfter, formatDate } from "date-fns";
import { z } from "zod";
import {
  CollectNodeFieldTypeEnum,
  CollectNodeFieldSchema,
} from "@aichat/flow-config";
import { isValueNumber } from "@aichat/aichat-lib";

export function dynamicFormResolver(
  fields: CollectNodeFieldSchema[],
): Resolver<Record<string, string | string[] | Date>> {
  return (values) => {
    const errors: Record<string, any> = {};

    for (const field of fields) {
      const { slot_name, type } = field;

      if (!slot_name) continue;

      const value = values[slot_name];

      // Common: required check
      if (value === undefined) {
        errors[slot_name] = { message: `Invalid value.` };
        continue;
      }

      switch (type) {
        case CollectNodeFieldTypeEnum.TEXT: {
          if (typeof value !== "string" || value.trim().length === 0) {
            errors[slot_name] = { message: `Please enter valid text.` };
          }
          break;
        }

        case CollectNodeFieldTypeEnum.NUMBER: {
          if (Array.isArray(value)) {
            errors[slot_name] = "Invalid value";
            break;
          }
          const isNumber = isValueNumber(value.toString());
          if (!isNumber) {
            errors[slot_name] = { message: `Please enter valid number.` };
          } else {
            const numValue = Number(value);
            const { minValue, maxValue, allowDecimal } = field;
            if (minValue && numValue < Number(minValue)) {
              errors[slot_name] = {
                message: `Must be more than ${minValue}`,
              };
            }
            if (maxValue && numValue > Number(maxValue)) {
              errors[slot_name] = {
                message: `Must be less than than ${maxValue}`,
              };
            }
            if (value.toString().includes(".") && !allowDecimal) {
              errors[slot_name] = {
                message: `Cannot have decimal value`,
              };
            }
          }
          break;
        }

        case CollectNodeFieldTypeEnum.EMAIL: {
          const { success } = z.string().email().safeParse(value);
          if (!success) {
            errors[slot_name] = { message: `Invalid Email` };
          }
          break;
        }

        case CollectNodeFieldTypeEnum.PHONE: {
          if (Array.isArray(value)) {
            errors[slot_name] = "Invalid value";
            break;
          }
          if (value.toString()[0] !== "+") {
            errors[slot_name] = {
              message: `Phone number must start with a country code.`,
            };
          }
          const onlyPhoneNo = value.toString().split("+")[1];
          if (!onlyPhoneNo || !isValueNumber(onlyPhoneNo)) {
            errors[slot_name] = { message: "Invalid phone number" };
          }
          if (field.countryCodes) {
            const isValid = field.countryCodes.some((code) => {
              return value.toString().startsWith(code);
            });

            if (!isValid) {
              errors[slot_name] = { message: "Country code not supported" };
            }
          }
          break;
        }

        case CollectNodeFieldTypeEnum.SELECT: {
          const { singleSelect, selectValues } = field;
          if (singleSelect) {
            if (Array.isArray(value)) {
              errors[slot_name] = { message: "Please select valid value" };
            }

            const isValid = selectValues.some((v) => v.value === value);
            if (!isValid)
              errors[slot_name] = { message: "Please select valid value" };
          } else {
            if (!Array.isArray(value)) {
              errors[slot_name] = { message: "Please select valid value" };
            }
            if ((value as string[]).length <= 0)
              errors[slot_name] = { message: "Please select valid value" };
            else {
              const isValid = selectValues.some((v) =>
                value.toString().includes(v.value),
              );
              if (!isValid) errors[slot_name] = "Please select valid value";
            }
          }
          break;
        }

        case CollectNodeFieldTypeEnum.TIME: {
          if (Array.isArray(value)) {
            errors[slot_name] = "Invalid value";
            break;
          }
          const { acceptedTimes } = field;
          if (acceptedTimes) {
            const parseTime = (str: string) =>
              parse(str, "hh:mm a", new Date(2000, 0, 1));
            const inputTime = parseTime(value.toString());
            const isInvalid = acceptedTimes.every(({ from, to }) => {
              const fromTime = parseTime(from);
              const toTime = parseTime(to);
              const crossesMidnight = isAfter(fromTime, toTime);
              return crossesMidnight
                ? isBefore(inputTime, fromTime) && isAfter(inputTime, toTime)
                : isBefore(inputTime, fromTime) || isAfter(inputTime, toTime);
            });

            if (isInvalid) {
              errors[slot_name] = { message: "Invalid time range" };
            }
          }
          break;
        }

        case CollectNodeFieldTypeEnum.DATE: {
          const { dateRange } = field;
          if (!value) {
            errors[slot_name] = {
              message: `Please enter valid date`,
            };
            continue;
          }
          const dateValue = value as Date;

          if (dateRange) {
            const inputDate = dateValue.toISOString();
            const { startDate, endDate } = dateRange;
            if (startDate && !isAfter(inputDate, startDate)) {
              errors[slot_name] = {
                message: `Date should be after ${formatDate(new Date(startDate), "do MMM yyyy")}`,
              };
            }
            if (endDate && !isBefore(inputDate, endDate)) {
              errors[slot_name] = {
                message: `Date should be before ${formatDate(new Date(endDate), "do MMM yyyy")}`,
              };
            }
          }
          break;
        }
      }
    }

    return {
      values: Object.keys(errors).length === 0 ? values : {},
      errors,
    };
  };
}
